import jsonata from 'jsonata';
import lodash from 'lodash';
import deepdash from 'deepdash';
import { format } from 'd3-format';
import { format as uikFormat } from '@appd/uik';
import { toHealthScatterSeries } from '../components/Scatter';
import { toHeatMapSeries } from '../components/HeatMap';
import { toHeatMapCategories } from '../components/HeatMap';
import { toHeatMapSeriesWithIdAsYAxis } from '../components/HeatMap';
import { statusToHealthClassName, statusToHealthColor, statusToHealthIcon, statusToHealthLabel } from '../health/index';
import axios from 'axios';
import { logger } from '../../utils';
import { IIterateeContext } from 'deepdash/es/IIterateeContext';

export interface ExpressionContext {
  parent: {
    path: string;
  };
}

export interface ExtractedExpression {
  key: string;
  value: unknown;
  valueExpStr: string;
  parent: unknown;
  path: string;
  context: ExpressionContext;
}

const _ = deepdash(lodash);

const isExpression = (s: unknown) => typeof s === 'string' && s.trim().startsWith('${') && s.trim().endsWith('}');

const getJsonataExpFromString = (s: string = '') => (isExpression(s) ? s.trim().slice(2, -1) : '');

const isExpressionPreviouslySeen = (exp: { value: unknown }, queue: unknown[]) =>
  _.find(queue, { value: exp.value }) !== undefined;

const httpget = function (args: { url: string; transformResult: string }) {
  return new Promise(function (resolve, reject) {
    axios({ url: args.url })
      .then(axiosResponseObject => axiosResponseObject.data)
      .then(async data => {
        if (args.transformResult) {
          const exp = jsonata(args.transformResult);
          const trx = await jsonataWithCallback(exp, data);
          return trx;
        }
        return data;
      })
      .then(trxData => resolve(trxData))
      .catch(error => resolve(error.response.data));
  });
};

const parseDateTime = function (arg: string) {
  return Date.parse(arg);
};

export const jsonataWithCallback = function (
  expr: string | jsonata.Expression,
  data: unknown,
  bindings?: Record<string, unknown> | undefined,
) {
  const expression = jsonata(expr as string);
  expression.assign('httpget', httpget);
  expression.assign('formatNumber', format);
  expression.assign('parseDateTime', parseDateTime);
  expression.assign('defaultsDeep', _.defaultsDeep);
  expression.assign('toHealthScatterSeries', toHealthScatterSeries);
  expression.assign('toHeatMapSeries', toHeatMapSeries);
  expression.assign('toHeatMapSeriesWithIdAsYAxis', toHeatMapSeriesWithIdAsYAxis);
  expression.assign('toHeatMapCategories', toHeatMapCategories);
  expression.assign('toNumberShort', uikFormat.number.short);
  expression.assign('formatDateTime', uikFormat.dateTime);
  expression.assign('statusToHealthClassName', statusToHealthClassName);
  expression.assign('statusToHealthLabel', statusToHealthLabel);
  expression.assign('statusToHealthColor', statusToHealthColor);
  expression.assign('statusToHealthIcon', statusToHealthIcon);

  return expression.evaluate(data, bindings, function (error: jsonata.JsonataError, response: unknown) {
    if (error) {
      return;
    }
    return response;
  });
};

const evaluateExpress = async (exp: string, context = {}) => {
  const jsonataExp = getJsonataExpFromString(exp);
  return await jsonataWithCallback(jsonataExp, context);
};

const extractExpressions = (
  acc: ExtractedExpression[] = [],
  value: unknown,
  key: string,
  parent: unknown,
  context: ExpressionContext,
): ExtractedExpression[] => {
  if (isExpression(value)) {
    acc.push({
      key,
      value,
      valueExpStr: getJsonataExpFromString(value as string),
      parent,
      path: context?.parent?.path ? `${context.parent.path}.${key}` : key,
      context,
    });
  }
  return acc;
};

export const evaluate = async (input: unknown): Promise<unknown | Record<string, unknown>> => {
  if (
    typeof input === 'boolean' ||
    typeof input === 'undefined' ||
    typeof input === 'number' ||
    typeof input === 'bigint'
  ) {
    return input;
  }

  if (typeof input === 'string') {
    if (isExpression(input)) {
      return await evaluateExpress(input, {});
    }
    return input;
  }

  if (_.isPlainObject(input)) {
    const output: Record<string, unknown> = _.cloneDeep(input) as Record<string, unknown>;

    const expQueue = _.reduceDeep(
      input,
      extractExpressions as (
        accumulator: unknown,
        value: unknown,
        key: string | number,
        parentValue: unknown,
        context: IIterateeContext,
      ) => any,
      [],
    );
    const previouslySeenExp = [];

    while (expQueue.length > 0) {
      const expressionObj = expQueue.shift();
      try {
        const value = await evaluateExpress(expressionObj.value, output);

        // value can point to another expression
        // {a: 'foo', b: '${a}'}
        if (isExpression(value)) {
          if (!isExpressionPreviouslySeen(expressionObj, previouslySeenExp)) {
            expQueue.push(expressionObj);
            previouslySeenExp.push(expressionObj);
          }
        } else {
          _.set(output, expressionObj.path, value);
          _.remove(previouslySeenExp, expressionObj);
        }
      } catch (error) {
        logger.log(
          `expression: unable to evaluate`,
          getJsonataExpFromString(expressionObj.value),
          JSON.stringify(output),
          error,
        );
        if (!isExpressionPreviouslySeen(expressionObj, previouslySeenExp)) {
          expQueue.push(expressionObj);
          previouslySeenExp.push(expressionObj);
        }
      }
    }

    if (previouslySeenExp.length > 0) {
      throw new Error('Circular dependency');
    }

    return output;
  }
};
